import { ref } from 'vue'
import { jsonapiClient } from '@/api/client'
import type { components, paths } from '@/api/schema'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { usePagination } from '@/composables/usePagination'
import { registerTypeIdentities } from '@/lib/typeIdentity'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'
import { useSeasonCatalog } from '@/composables/useSeasonCatalog'

type JobTemplate = components['schemas']['JobTemplateResource']
type TemplateStep = components['schemas']['TemplateStepResource']
export type TemplateExtraVisit = components['schemas']['TemplateExtraVisitResource']
type QueryParams = paths['/api/job_templates']['get']['parameters']['query']

// A crew's two capability lanes — « Travaux » / « Soumissions », the board
// lens's vocabulary.
export type CapabilityLane = 'work' | 'quotes'

export type JobTemplateFormValues = {
  name: string
  description?: string | null
  // The evaluation slot: null = "use the kind fallback", never zero.
  evaluation_duration_minutes?: number | null
  // The type's default intake lane (a visible prefill of soumission vs travaux).
  evaluation_by_default?: boolean
  // What the look-see is worth — a DecimalString on the wire (null = gratuite).
  // « Offerte à la signature »: a proposal born on this type carries the fee as
  // a 100%-discounted line, so the fee is only ever collected when the deal
  // does not sign.
  evaluation_fee?: string | null
  // Chosen identity — null = "Auto" (hash color / no icon). See lib/typeIdentity.
  color?: string | null
  icon?: string | null
  // The type's weather DEFAULT, under any per-step value — it covers work
  // created outside a programme, which carries no step. {} clears it.
  weather_sensitivity?: components['schemas']['WeatherSensitivity']
}

export function useJobTemplates() {
  const { t } = useI18n()
  const { handleError } = useApiErrorHandler()

  const currentJobTemplate = ref<JobTemplate | null>(null)
  const templateSteps = ref<TemplateStep[]>([])
  // « Visites supplémentaires » presets — read with the type here, written
  // through useTemplateExtraVisits.
  const templateExtraVisits = ref<TemplateExtraVisit[]>([])
  const detailLoading = ref(false)

  const pagination = usePagination<JobTemplate, QueryParams>(async (params) => {
    try {
      const response = await jsonapiClient.get('/api/job_templates', { params })
      const data = response.data.data ?? []
      registerTypeIdentities(data)
      return {
        data,
        meta: response.data.meta,
      }
    } catch (error) {
      handleError(error)
      return null
    }
  })

  const fetchJobTemplate = async (id: string) => {
    detailLoading.value = true
    try {
      const response = await jsonapiClient.get('/api/job_templates/{id}', {
        path: { id },
        params: { include: { template_steps: true, template_extra_visits: true } },
      })
      currentJobTemplate.value = response.data.data
      registerTypeIdentities([response.data.data])
      const included = response.data.included ?? []
      templateSteps.value = included
        .filter((r): r is TemplateStep => r.type === 'template_step')
        .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))
      templateExtraVisits.value = included
        .filter((r): r is TemplateExtraVisit => r.type === 'template_extra_visit')
        .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))
      return currentJobTemplate.value
    } catch (error) {
      handleError(error)
      return null
    } finally {
      detailLoading.value = false
    }
  }

  // `crews` = the type's crews per lane at birth (none is legal: the type is then
  // flagged « Aucune équipe » until someone gives it some).
  const create = async (data: JobTemplateFormValues, crews?: { work?: string[], quotes?: string[] }) => {
    const rows = (ids: string[]) => ({ data: ids.map(id => ({ type: 'squad' as const, id })) })
    const relationships = crews
      ? { work_squads: rows(crews.work ?? []), quote_squads: rows(crews.quotes ?? []) }
      : undefined
    try {
      const response = await jsonapiClient.post('/api/job_templates', {
        data: { type: 'job_template' as const, attributes: data, relationships },
      })
      const created = response.data.data
      registerTypeIdentities([created])
      pagination.prependItem(created)
      toast.success(t('templates.createSuccess'))
      return created
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // `silent`: a page that edits each value where it reads it (the type page)
  // saves on every field — a toast per field is noise; errors still toast.
  const update = async (id: string, data: Partial<JobTemplateFormValues>, options?: { silent?: boolean }) => {
    try {
      const response = await jsonapiClient.patch('/api/job_templates/{id}', {
        data: { type: 'job_template' as const, attributes: data },
      }, { path: { id } })
      const updated = response.data.data
      registerTypeIdentities([updated])
      if (currentJobTemplate.value?.id === id) currentJobTemplate.value = updated
      pagination.replaceItem(id, updated)
      if (!options?.silent) toast.success(t('templates.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // « Équipes » — the crews granted this type, ONE LANE AT A TIME: the same rows
  // the crew page writes from the crew's side. A pure relationship write (the
  // server's tri-state: present replaces, [] = no crew), so it has its own door
  // rather than being smuggled into the attributes patch. The relationship object
  // is built per lane rather than by computed key — an exact key keeps it typed.
  const updateTypeSquads = async (id: string, lane: CapabilityLane, squadIds: string[], options?: { silent?: boolean }) => {
    const rows = squadIds.map(sid => ({ type: 'squad' as const, id: sid }))
    const relationships = lane === 'quotes'
      ? { quote_squads: { data: rows } }
      : { work_squads: { data: rows } }
    try {
      const response = await jsonapiClient.patch('/api/job_templates/{id}', {
        data: { type: 'job_template' as const, relationships },
      }, { path: { id } })
      const updated = response.data.data
      if (currentJobTemplate.value?.id === id) currentJobTemplate.value = updated
      if (!options?.silent) toast.success(t('templates.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // « Équipes » NUMBERS — a lane's numbered crews in fill order: n° 1 is filled
  // first, n° 2 gets what n° 1 could not take, the lane's other crews share the
  // rest. The whole order in one write; [] = nobody numbered.
  const updateTypeSquadRanks = async (id: string, lane: CapabilityLane, squadIds: string[], options?: { silent?: boolean }) => {
    const rows = squadIds.map(sid => ({ type: 'squad' as const, id: sid }))
    const relationships = lane === 'quotes'
      ? { ranked_quote_squads: { data: rows } }
      : { ranked_work_squads: { data: rows } }
    try {
      const response = await jsonapiClient.patch('/api/job_templates/{id}', {
        data: { type: 'job_template' as const, relationships },
      }, { path: { id } })
      const updated = response.data.data
      if (currentJobTemplate.value?.id === id) currentJobTemplate.value = updated
      if (!options?.silent) toast.success(t('templates.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  /**
   * « Périodes » — WHEN this type may be worked. Same tri-state door as the
   * crew lanes: sending the relationship REPLACES the whole set, and [] is
   * meaningful — it clears back to year-round.
   *
   * ⚠ The attached périodes INTERSECT: the type must satisfy every one of them
   * (« Été 2026 » AND « aération ≤ 15 oct »), so this is a set, not a choice.
   */
  const updateWorkPeriods = async (id: string, workPeriodIds: string[], options?: { silent?: boolean }) => {
    const rows = workPeriodIds.map(pid => ({ type: 'work_period' as const, id: pid }))
    try {
      const response = await jsonapiClient.patch('/api/job_templates/{id}', {
        data: { type: 'job_template' as const, relationships: { work_periods: { data: rows } } },
      }, { path: { id } })
      const updated = response.data.data
      if (currentJobTemplate.value?.id === id) currentJobTemplate.value = updated
      useSeasonCatalog().invalidate() // the season menus read a shared copy of type → seasons
      if (!options?.silent) toast.success(t('templates.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // The program's whole order in one write. « Ajouter une visite » only ever
  // appends, so this is also the only way to put back a first visit deleted by
  // mistake: add it, drag it up. Optimistic — the row has already moved under
  // the cursor, and a round-trip would snap it back and forth — then re-read,
  // because POSITIONS are server-owned and the ordinal badge prints them.
  // Silent on success: a toast per drag is noise.
  const updateStepOrder = async (id: string, orderedIds: string[]) => {
    const previous = templateSteps.value
    templateSteps.value = orderedIds
      .map(sid => previous.find(step => step.id === sid))
      .filter((step): step is TemplateStep => !!step)
    try {
      await jsonapiClient.patch('/api/job_templates/{id}', {
        data: { type: 'job_template' as const, attributes: { template_step_order: orderedIds } },
      }, { path: { id } })
      await fetchJobTemplate(id)
      return true
    } catch (error) {
      templateSteps.value = previous
      handleError(error)
      return false
    }
  }

  const remove = async (id: string) => {
    try {
      await jsonapiClient.delete('/api/job_templates/{id}', { path: { id } })
      pagination.removeItem(id)
      toast.success(t('templates.deleteSuccess'))
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

  return {
    jobTemplates: pagination.data,
    loading: pagination.loading,
    loadingMore: pagination.loadingMore,
    hasMore: pagination.hasMore,
    meta: pagination.meta,
    fetchJobTemplates: pagination.fetchPage,
    loadMoreJobTemplates: pagination.loadMore,
    currentJobTemplate,
    templateSteps,
    templateExtraVisits,
    detailLoading,
    fetchJobTemplate,
    create,
    update,
    updateTypeSquads,
    updateTypeSquadRanks,
    updateWorkPeriods,
    updateStepOrder,
    remove,
  }
}
